import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getCustomer, ratingAvg1 } from "../../include/function";
import { api, baseURL, customerReqConfig } from "../../include/api";
import { useNotification } from "../../Toast/ToastProvider";
import { ERROR, SUCCESS } from "../../include/constant";
import { useCart } from "../context/UserCartCountContext";
import { addToCartHandler, loadCartData } from "../../helper/cartUtils";
import { ShoppingCart, Trash } from "react-feather";

const WishListSection = () => {
  const { fetchCartCount, setTotalWishlistItems } = useCart();
  const customer = getCustomer();
  const [fetchLoading, setFetchLoading] = useState(true);
  const [wishlistData, setWishlistData] = useState({});
  const [cartData, setCartData] = useState({});
  const [visibleCount, setVisibleCount] = useState(10);
  
  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + 10);
  };
  
  const toast = useNotification();

  useEffect(() => {
    getCartData();
    getWishlist();
  }, []);

  const getCartData = () => {
    if (customer) {
      api
        .get("/cart/?customerId=" + customer.id, customerReqConfig())
        .then((response) => {
          if (response.status === 200) {
            setCartData(response.data.data || []);
          }
        })
        .catch(() => {
          setCartData([]);
        });
    } else {
      setCartData([]);
    }
  };

  const getWishlist = () => {
    setFetchLoading(true);
    api
      .get("/wishlist/?customerId=" + customer.id, customerReqConfig())
      .then((response) => {
        if (response.status === 200) {
          setWishlistData(response.data.data || {});
        }
      })
      .catch(() => {
        setWishlistData({});
      })
      .finally(() => setFetchLoading(false));
  };

  return (
    <div className="">
      {!fetchLoading && (!wishlistData?.products || wishlistData.products.length === 0) ? (
        <section className="cart py-80">
          <div className="container container-lg text-center">
            <h3>No items in your wishlist</h3>
          </div>
        </section>
      ) : (
        <div>
          <div className="row gy-2 px-16 py-40 my-80">
            <div className="col-xl-12 col-lg-12">
              <div className="cart-table border border-gray-100 rounded-16 px-40 py-40">
                <div className="overflow-x-auto scroll-sm scroll-sm-horizontal">
                  <div className="d-flex justify-content-between">
                    <h6 className="text-xl mb-32 fw-bold ms-80">MY WISHLIST</h6>
                    <h6 className="text-xl mb-32 fw-bold me-80">{`${wishlistData?.products?.length} ITEMS`}</h6>
                  </div>
                  {wishlistData?.products?.slice(0, visibleCount).map((product, index) => {
                    // Skip invalid products
                    if (!product || typeof product !== "object") return null;
                    return (
                      <WishListItem
                        key={product.id || index}
                        product={product}
                        toast={toast}
                        customer={customer}
                        fetchWishList={getWishlist}
                        cartData={cartData}
                        fetchcart={getCartData}
                        fetchCartCount={fetchCartCount}
                        setTotalWishlistItems={setTotalWishlistItems}
                      />
                    );
                  })}
                  {wishlistData?.products?.length > visibleCount && (
                    <div className="text-center my-32">
                      <button
                        className="btn btn-main text-white px-40 py-12 rounded-16 fw-bold"
                        onClick={handleLoadMore}
                      >
                        Load More
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WishListSection;

// --- WishListItem Component ---
const WishListItem = ({ product, fetchWishList, cartData, customer, fetchcart, fetchCartCount, toast, setTotalWishlistItems }) => {
  const [addedToCartMap, setAddedToCartMap] = useState({});
  const { fetchCartCount: contextFetchCartCount, setTotalWishlistItems: contextSetTotalWishlistItems } = useCart();
  const [loading, setLoading] = useState(null);
  const navigate = useNavigate();
  const notification = useNotification();
  const [allReviewData, setAllReviewData] = useState([]);

  // Normalize discount modes to lowercase for safe comparison
  const b2bMode = (product.b2bDiscountMode || "").toLowerCase();
  const b2cMode = (product.b2cDiscountMode || "").toLowerCase();
  const basePrice = parseFloat(product.price) || 0;

  // Calculate B2B discounted price
  let b2bDiscountUnitPrice = basePrice;
  if (b2bMode === "percent") {
    const disc = parseFloat(product.b2bDiscountValue) || 0;
    b2bDiscountUnitPrice = basePrice - (basePrice * disc) / 100;
  } else if (b2bMode === "amount") {
    const disc = parseFloat(product.b2bDiscountValue) || 0;
    b2bDiscountUnitPrice = basePrice - disc;
  }

  // Calculate B2C discounted price
  let b2cDiscountUnitPrice = basePrice;
  if (b2cMode === "percent") {
    const disc = parseFloat(product.b2cDiscountValue) || 0;
    b2cDiscountUnitPrice = basePrice - (basePrice * disc) / 100;
  } else if (b2cMode === "amount") {
    const disc = parseFloat(product.b2cDiscountValue) || 0;
    b2cDiscountUnitPrice = basePrice - disc;
  }

  // Round & format
  const b2bSubTotal = Math.round(b2bDiscountUnitPrice).toFixed(2);
  const b2cSubTotal = Math.round(b2cDiscountUnitPrice).toFixed(2);

  // Determine customer-specific values
  const isB2C = !customer || customer.customerType === "individual";
  const displayPrice = isB2C ? b2cSubTotal : b2bSubTotal;

  const custDiscVal = isB2C ? product.b2cDiscountValue : product.b2bDiscountValue;
  const custDiscMode = isB2C 
    ? (b2cMode === "percent" ? "%" : "₹") 
    : (b2bMode === "percent" ? "%" : "₹");

  // Load cart status
  useEffect(() => {
    if (customer?.id) {
      loadCartData(customer.id, () => {}, setAddedToCartMap);
    }
  }, [customer?.id]);

  // Listen for cart updates
  useEffect(() => {
    const handleCartUpdate = () => {
      if (customer?.id) {
        loadCartData(customer.id, () => {}, setAddedToCartMap);
      }
    };
    window.addEventListener("cartUpdated", handleCartUpdate);
    return () => window.removeEventListener("cartUpdated", handleCartUpdate);
  }, [customer?.id]);

  // Fetch reviews
  useEffect(() => {
    api
      .get("/rating_and_review/", customerReqConfig())
      .then((response) => {
        if (response.status === 200) {
          setAllReviewData(response.data.data || []);
        }
      })
      .catch((error) => {
        console.error("Error fetching reviews:", error);
      });
  }, []);

  // Add to cart
  const handleAddToCart = (prod) => {
    addToCartHandler({
      product: prod,
      customer,
      setAddedToCartMap,
      setSavedForLaterData: () => {}, // dummy since not used here
      fetchCartCount: fetchCartCount || contextFetchCartCount,
      toast: toast || notification,
      navigate,
      setLoading,
      savedForLaterData: { products: [] },
    });
  };

  // Remove from wishlist
  const handleRemoveItemFromWishlist = () => {
    if (!product?.id || !customer?.id) return;
    setLoading(product.id);
    api
      .delete(`/wishlist/?customerId=${customer.id}&productId=${product.id}`, customerReqConfig())
      .then((response) => {
        if (response.status === 200) {
          fetchWishList();
          (setTotalWishlistItems || contextSetTotalWishlistItems)((prev) => Math.max(prev - 1, 0));
          (toast || notification)({ type: SUCCESS, message: response.data.message || "Removed from wishlist" });
        }
      })
      .catch((error) => {
        (toast || notification)({
          type: ERROR,
          message: error.response?.data?.message || error.message || "Failed to remove item",
        });
      })
      .finally(() => {
        setLoading(null);
      });
  };

  // Reviews
  const productReviews = allReviewData?.filter((r) => r.productId === product.id) || [];

  const isAdded = !!addedToCartMap[product.id];

  // Guard against missing product
  if (!product || !product.id) return null;

  return (
    <div className="card cart-table border border-gray-100 rounded-16 mb-16 p-16 ms-80 me-80">
      <div className="row g-0">
        {/* Product Image */}
        <div
          className="col-12 col-md-3 d-flex align-items-center justify-content-center rounded-16 p-3 mb-3 mb-md-0"
          style={{ backgroundColor: "#F8F8F8", width: "20%" }}
        >
          <Link to={"/product-details/" + product.id}>
            <img
              src={baseURL + "/" + (product.mainImage || "")}
              alt={product.name || "Product"}
              className="img-fluid rounded-8"
              style={{ maxHeight: "120px", objectFit: "contain" }}
            />
          </Link>
        </div>

        {/* Product Details */}
        <div className="col-12 col-md-6 border border-gray-100 border-start-0 border-md-start-0 border-end-1 border-top-0 border-bottom-0 border-md-end-0 p-0 mb-3 mb-md-0">
          <div className="card-body d-flex flex-column h-100">
            <div className="flex-grow-1">
              <h6 className="card-title text-md fw-bold mb-2">
                <Link to={"/product-details/" + product.id} className="link text-line-2">
                  {product.name?.toUpperCase() || "Unnamed Product"}
                </Link>
              </h6>

              {/* Reviews */}
              {productReviews.length > 0 && (
                <div className="flex-align gap-8 mb-2">
                  <span className="fw-medium text-neutral-600">
                    <div className="badge bg-success">
                      {ratingAvg1(productReviews)} <i className="ph-fill ph-star" />
                    </div>
                  </span>
                  <span className="fw-medium text-gray-500">({productReviews.length})</span>
                </div>
              )}
              <br />

              {/* Price */}
              <div className="d-flex hstack gap-8">
                <div className="text-lg fw-bold">₹ {displayPrice}</div>
                {parseFloat(custDiscVal) > 0 && (
                  <span className="text-sm" style={{ color: "#ff6600" }}>
                   Discount of {custDiscVal} {custDiscMode}
                  </span>
                )}
              </div>

              <label className="text-sm fw-medium">M.R.P:&nbsp;</label>
              <span className="text-gray-500 text-md fw-bold" style={{ textDecoration: "line-through" }}>
                ₹ {basePrice.toFixed(2)}
              </span>
              <div className="text-gray-500 text-md fw-medium">Inclusive of all taxes</div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="col-12 col-md-3 d-flex flex-column align-items-end ps-16">
          <button
            className={
              isAdded
                ? "btn btn-sm btn-main text-white mt-32 mb-16"
                : "btn btn-sm text-neutral-600 flex-center hover-bg-main-600 hover-text-white mt-32 mb-16"
            }
            style={{ width: "200px", backgroundColor: "#ddd", border: "none" }}
            onClick={() => {
              if (isAdded) {
                navigate("/cart");
              } else {
                handleAddToCart(product);
              }
            }}
            disabled={loading === product.id}
          >
            {loading === product.id ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                Adding...
              </>
            ) : isAdded ? (
              <>
                &nbsp;&nbsp;Go To Cart&nbsp;&nbsp;&nbsp;
                <ShoppingCart size={"20px"} />
              </>
            ) : (
              <>
                Add to Cart&nbsp;&nbsp;&nbsp;
                <ShoppingCart size={"20px"} />
              </>
            )}
          </button>

          <button
            type="button"
            className="btn btn-sm text-neutral-600 flex-center hover-bg-main-600 hover-text-white"
            style={{ width: "200px", backgroundColor: "#ddd", border: "none" }}
            disabled={loading === product.id}
            onClick={handleRemoveItemFromWishlist}
          >
            {loading === product.id ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                Removing...
              </>
            ) : (
              <>
                &nbsp;&nbsp;Remove&nbsp;&nbsp; <Trash size={"20px"} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};